import { useContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { Tabs } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { ScrollingTabsList } from "@/components/Tabs";
import { TextWrapper } from "@/components/TextWrapper";
import { ProfileContext } from "@/contexts/profile";
import DxPlanTabPanel from "@/encounters/components/DxPlan/DxPlanTabPanel";
import { EncounterProceduresProvider } from "@/encounters/contexts/EncounterProceduresContext";
import { EncounterAnswersFormType } from "@/encounters/zod-schemas/encounter-answers";
import { NewbornIPPEncounterFormType } from "@/encounters/zod-schemas/newborn-ipp-encounter";
import { DevelopmentFeatureFlags, FeatureGate } from "@/feature-flags";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";

import classes from "./EncounterTabs.module.css";

export enum EncounterTabsEnum {
  SUBJECTIVE_CLINICAL_NOTES = "subjective-clinical-notes",
  OBJECTIVE_CLINICAL_NOTES = "objective-clinical-notes",
  DX_ORDERS_PLAN = "dx-orders-plan",
  BILLING = "billing",
  PROCEDURES = "procedures",
}

interface EncounterTabsProps {
  form: UseFormReturnType<EncounterAnswersFormType | NewbornIPPEncounterFormType>;
  subjectiveChildren?: React.ReactNode;
  objectiveChildren?: React.ReactNode;
  billingChildren?: React.ReactNode;
  proceduresChildren?: React.ReactNode;
  isEncounterActive: boolean;
  isAmendment?: boolean;
  formInitialValues?: EncounterAnswersFormType;
  onProcedureDrawerOpen?: () => void;
  activeStartTab?: EncounterTabsEnum;
  value?: EncounterTabsEnum;
  onChange?: (value: EncounterTabsEnum) => void;
}

const EncounterTabs = ({
  form,
  subjectiveChildren,
  objectiveChildren,
  billingChildren,
  proceduresChildren,
  isEncounterActive,
  isAmendment,
  onProcedureDrawerOpen,
  activeStartTab = EncounterTabsEnum.OBJECTIVE_CLINICAL_NOTES,
  value: controlledValue,
  onChange: controlledOnChange,
}: EncounterTabsProps) => {
  const { clientId, encounterId } = useParams<{ clientId: string; encounterId: string }>();
  const [searchParams] = useSearchParams();
  const openedFromServicesBillingDeepLink = searchParams.get("tab") === EncounterTabsEnum.BILLING;

  const { isPatient } = useContext(ProfileContext);
  const { hasBillerRole } = usePractitionerPermissions();
  // What "Billing is open" means, in one place: the tab control, the deep link and the clamp below
  // all read this, so none of them can drift from the others on who gets in and when.
  const isBillingReachable = !isPatient && hasBillerRole && !isEncounterActive;

  const [internalTab, setInternalTab] = useState<EncounterTabsEnum>(activeStartTab);
  // The deep link only decides where the tab opens. Once the user picks a tab themselves the link
  // stops applying, otherwise the derivation below would pull them back to Billing on every render
  // for as long as ?tab=billing sits in the URL.
  const [hasLeftBillingDeepLink, setHasLeftBillingDeepLink] = useState(false);
  const isControlled = controlledValue !== undefined && controlledOnChange !== undefined;
  const requestedTab = isControlled ? controlledValue! : internalTab;
  const setActiveTab = isControlled ? controlledOnChange! : setInternalTab;

  // The deep link and the encounter's status arrive on different renders: the encounter is fetched,
  // so isEncounterActive reads true until it lands. Selecting Billing on a plain derivation rather
  // than a mount-time initializer lets the link land as soon as the status says it may, and hands
  // the user back a reachable tab the moment Billing closes under them (signing gets reverted, the
  // biller role goes away) without a corrective render in between.
  let activeTab = requestedTab;
  if (openedFromServicesBillingDeepLink && isBillingReachable && !hasLeftBillingDeepLink) {
    activeTab = EncounterTabsEnum.BILLING;
  } else if (requestedTab === EncounterTabsEnum.BILLING && !isBillingReachable) {
    // activeStartTab is the fallback, but it must land on a tab that is actually open: a caller
    // passing BILLING as the start tab would otherwise send an unreachable viewer right back to it.
    activeTab =
      activeStartTab === EncounterTabsEnum.BILLING
        ? EncounterTabsEnum.OBJECTIVE_CLINICAL_NOTES
        : activeStartTab;
  }

  useEffect(() => {
    if (!openedFromServicesBillingDeepLink) return;

    requestAnimationFrame(() => {
      document
        .querySelector('[data-testid="encounter-tabs"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [openedFromServicesBillingDeepLink]);

  const handleTabVariant = (tab: EncounterTabsEnum) => {
    return activeTab === tab ? "bold" : "default";
  };

  return (
    // The procedures panel and the Dx tab's Diagnoses section both read the encounter's clinical
    // Procedures and Conditions, and Mantine keeps every panel mounted, so the read is owned here
    // rather than by each of them.
    <EncounterProceduresProvider clientId={clientId} encounterId={encounterId}>
      <Tabs
        radius="md"
        color="gray"
        variant="pills"
        value={activeTab}
        data-testid="encounter-tabs"
        onChange={(value) => {
          setHasLeftBillingDeepLink(true);
          setActiveTab(value as EncounterTabsEnum);
        }}
      >
        <ScrollingTabsList className={classes.tabsList}>
          {subjectiveChildren && (
            <Tabs.Tab
              className={classes.tab}
              data-testid="encounter-tab-subjective"
              value={EncounterTabsEnum.SUBJECTIVE_CLINICAL_NOTES}
            >
              <TextWrapper variant={handleTabVariant(EncounterTabsEnum.SUBJECTIVE_CLINICAL_NOTES)}>
                Subjective
              </TextWrapper>
            </Tabs.Tab>
          )}
          <Tabs.Tab
            className={classes.tab}
            data-testid="encounter-tab-objective"
            value={EncounterTabsEnum.OBJECTIVE_CLINICAL_NOTES}
          >
            <TextWrapper variant={handleTabVariant(EncounterTabsEnum.OBJECTIVE_CLINICAL_NOTES)}>
              Objective
            </TextWrapper>
          </Tabs.Tab>
          {proceduresChildren && (
            <FeatureGate name={DevelopmentFeatureFlags.PracticeBilling}>
              <Tabs.Tab
                className={classes.tab}
                value={EncounterTabsEnum.PROCEDURES}
                data-testid="encounter-tab-procedures"
              >
                <TextWrapper variant={handleTabVariant(EncounterTabsEnum.PROCEDURES)}>
                  Procedures
                </TextWrapper>
              </Tabs.Tab>
            </FeatureGate>
          )}
          <Tabs.Tab
            className={classes.tab}
            value={EncounterTabsEnum.DX_ORDERS_PLAN}
            data-testid="encounter-tab-dx-orders-plan"
          >
            <TextWrapper variant={handleTabVariant(EncounterTabsEnum.DX_ORDERS_PLAN)}>
              Dx, Orders & Plan
            </TextWrapper>
          </Tabs.Tab>
          {/* Hidden outright for a client rather than disabled: disabled still shows the tab exists,
          and a client should not know there is a Billing tab on their own encounter at all. */}
          {!isPatient && (
            <Tabs.Tab
              className={classes.tab}
              disabled={!isBillingReachable}
              value={EncounterTabsEnum.BILLING}
              data-testid="encounter-tab-billing"
            >
              <TextWrapper variant={handleTabVariant(EncounterTabsEnum.BILLING)}>
                Billing
              </TextWrapper>
            </Tabs.Tab>
          )}
        </ScrollingTabsList>

        {subjectiveChildren && (
          <Tabs.Panel value={EncounterTabsEnum.SUBJECTIVE_CLINICAL_NOTES}>
            {subjectiveChildren}
          </Tabs.Panel>
        )}

        {objectiveChildren && (
          <Tabs.Panel value={EncounterTabsEnum.OBJECTIVE_CLINICAL_NOTES}>
            {objectiveChildren}
          </Tabs.Panel>
        )}

        {proceduresChildren && (
          <Tabs.Panel
            value={EncounterTabsEnum.PROCEDURES}
            data-testid="encounter-tab-panel-procedures"
          >
            {proceduresChildren}
          </Tabs.Panel>
        )}

        {/* Mantine mounts every panel whether or not its tab is reachable, and a disabled tab is not
        an authorization boundary: a hand-edited ?tab=billing URL selects this panel programmatically.
        Withholding the panel keeps the billing data out of the page for anyone the tab is shut to. */}
        {billingChildren && isBillingReachable && (
          <Tabs.Panel value={EncounterTabsEnum.BILLING}>{billingChildren}</Tabs.Panel>
        )}

        <Tabs.Panel value={EncounterTabsEnum.DX_ORDERS_PLAN}>
          <DxPlanTabPanel
            form={form}
            isAmendment={isAmendment}
            data-testid="dx-plan-tab-panel"
            isEncounterActive={isEncounterActive}
            // Where the Diagnoses section's New, Edit and Add Procedure land. Only offered when this
            // encounter has a Procedures panel to land on.
            onOpenProceduresTab={
              proceduresChildren ? () => setActiveTab(EncounterTabsEnum.PROCEDURES) : undefined
            }
            onProcedureDiagnosisEditClick={() => {
              setActiveTab(EncounterTabsEnum.OBJECTIVE_CLINICAL_NOTES);
              if (onProcedureDrawerOpen) onProcedureDrawerOpen();
            }}
          />
        </Tabs.Panel>
      </Tabs>
    </EncounterProceduresProvider>
  );
};

export default EncounterTabs;
